import { Component, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  addAgentNote,
  updateAgentNote,
  createAgentCommissionCheque,
  createAgentPortalAccount,
  deleteAgentDocument,
  deleteAgent,
  deleteAgentNote,
  deleteCommissionPayment,
  deleteCommissionPaymentBatch,
  getAgent,
  getAgentDocumentDownload,
  getAgentCommissionPayments,
  getAgencyFields,
  getAgentsMeta,
  getStudents,
  linkStudentToAgent,
  patchAgent,
  revealAgentSin,
  unlinkStudentFromAgent,
  uploadAgentDocument,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { validateAgencyContactMethods } from '@/lib/agencyContact';
import { fullRegionName } from '@/lib/addressRegions';
import { agencyProgramAbbreviation } from '@/lib/agencyDashboard';
import {
  formatDateTimeValue,
  formatDateValue,
  localDateInputValue,
  parseDateValue,
  toDateInputValue,
} from '@/lib/dates';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import AgencyProfileForm from '@/components/admin/AgencyProfileForm';
import AgencyDashboardEditor from '@/components/admin/AgencyDashboardEditor';
import NotesThread from '@/components/admin/NotesThread';
import { notesAfterDelete } from '@/lib/noteThreads';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import { ProfileField as InfoRow } from '@/components/admin/ProfileField';
import { parseMentions } from '@/lib/mentions';
import {
  ProfileLayoutToolbar,
  ProfileSection as LayoutSection,
  ProfileSectionGrid,
  useProfileLayout,
} from '@/components/admin/ProfileLayout';
import AgencyAgreementCard from '@/components/agency/AgencyAgreementCard';
import ChequeEditorDialog from '@/components/admin/ChequeEditorDialog';
import CommissionChequeBuilder from '@/components/admin/CommissionChequeBuilder';
import ProfileHero from '@/components/profile/ProfileHero';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarClock,
  CalendarDays,
  Compass,
  CreditCard,
  Download,
  FileText,
  Globe2,
  Handshake,
  Hash,
  KeyRound,
  Link as LinkIcon,
  Loader2,
  Mail,
  MapPin,
  PencilLine,
  Phone,
  Plus,
  Send,
  ShoppingBag,
  StickyNote,
  Target,
  Trash2,
  Unlink,
  Upload,
  UserPlus,
  UserRound,
  Users,
} from 'lucide-react';

/* Tokens */

const inputClass =
  'border-[rgba(6,43,26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UNASSIGNED = '__unassigned__';
const SHORT_PROGRAM_TERM = 'short';
const NONE_PLACEHOLDER = 'Not set';
const PROGRAM_OPTIONS = [
  'Business Communication Pre-requisite Program',
  'French as a Second Language (FSL)',
  'French Test Preparation (TCF & TEF)',
  'Global Management in Hospitality Diploma',
  'Hospitality Operations Diploma',
  'International Esports Management Diploma',
  'Next-Gen Applied AI Mastery',
  'Video Content Creation and Social Media Management',
];

const DEFAULT_META = {
  tiers: [0, 1, 2, 3],
  tierLabels: {
    0: 'No agreement signed and no students sent.',
    1: 'Agreement signed, OR up to 1 student.',
    2: 'Agreement signed AND 2-5 students.',
    3: 'Agreement signed AND 6+ students.',
  },
  statuses: ['pending', 'new', 'warm', 'hot', 'reserve', 'cold', 'signed'],
  markets: ['CN', 'IN', 'PH', 'TW', 'KR', 'VN', 'HK', 'INT'],
  programs: PROGRAM_OPTIONS,
  programsOfInterest: PROGRAM_OPTIONS,
  programRecords: [],
  programAbbreviations: {},
  agencyTypes: ['Domestic', 'International', 'Personal'],
  countries: [],
  owners: ['Jody', 'Harman', 'Dogukan', 'Karissa'],
};

/* Shared with the card grid (Commit B) so the badge colors match exactly. */
const STATUS_STYLES = {
  pending: 'border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#7A5A05]',
  new: 'border-[#062B1A]/15 bg-[#062B1A]/5 text-[#062B1A]/70',
  warm: 'border-amber-200 bg-amber-50 text-amber-700',
  hot: 'border-red-200 bg-red-50 text-red-700',
  reserve: 'border-orange-200 bg-orange-50 text-orange-700',
  cold: 'border-slate-200 bg-slate-50 text-slate-600',
  signed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

const TIER_BADGE_STYLES = {
  0: 'border-slate-300 bg-slate-50 text-slate-700',
  1: 'border-[#B08D57]/45 bg-[#B08D57]/10 text-[#6E4F24]',
  2: 'border-slate-400/50 bg-slate-100 text-slate-700',
  3: 'border-[#D4AF37]/55 bg-[#D4AF37]/14 text-[#7A5A05]',
};

const PROVINCE_STATE_COUNTRIES = new Set(['CA', 'US']);

/* Helpers */

function labelize(value) {
  return (
    String(value || '')
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ') || NONE_PLACEHOLDER
  );
}

function formatPercentNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0%';
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  });
}

function parseDateOnly(value) {
  return parseDateValue(toDateInputValue(value));
}

function formatDate(value) {
  return formatDateValue(value, {
    fallback: NONE_PLACEHOLDER,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(value) {
  return formatDateTimeValue(value, { fallback: '', invalidFallback: '' });
}

function todayDateInput() {
  return localDateInputValue();
}

function formatNoteDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function isOverdue(value) {
  const parsed = parseDateOnly(value);
  if (!parsed) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed.getTime() < today.getTime();
}

function agencyInitials(value) {
  const name = String(value || '').trim();
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
}

function tierLabel(tier, tierLabels) {
  const key = String(Number(tier ?? 0));
  return tierLabels?.[key] || tierLabels?.[Number(key)] || `Tier ${key}`;
}

function present(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function isValidWebsite(value) {
  const text = String(value || '').trim();
  if (!text) return true;
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function compactList(value, limit = 100) {
  return safeList(value)
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .slice(0, limit);
}

function buildGeneralContactInfo(form) {
  const rows = [
    form.communicationChannels?.length ? `Communication channels: ${compactList(form.communicationChannels).join(', ')}` : '',
    form.communicationChannelOther ? `Other communication channel: ${form.communicationChannelOther.trim()}` : '',
    form.natureOfBusiness ? `Nature of business: ${form.natureOfBusiness}` : '',
    form.natureOfBusinessOther ? `Other business type: ${form.natureOfBusinessOther.trim()}` : '',
    form.numberOfEmployees ? `Employees: ${form.numberOfEmployees}` : '',
    form.studentsSentToCanadaPast12Months ? `Students sent to Canada in past 12 months: ${form.studentsSentToCanadaPast12Months}` : '',
    form.studentsAnticipatedForMccNext12Months ? `Students anticipated for MCC in next 12 months: ${form.studentsAnticipatedForMccNext12Months}` : '',
    form.studentSourceCountries?.length ? `Top student countries: ${compactList(form.studentSourceCountries, 3).join(', ')}` : '',
    form.targetEducationLevels?.length ? `Target education levels: ${compactList(form.targetEducationLevels, 3).join(', ')}` : '',
    form.promotionalChannels?.length ? `Promotional channels: ${compactList(form.promotionalChannels).join(', ')}` : '',
    form.howDidYouHearAboutMcc ? `Heard about MCC: ${form.howDidYouHearAboutMcc}` : '',
    form.howDidYouHearAboutMccOther ? `Other source: ${form.howDidYouHearAboutMccOther.trim()}` : '',
  ];
  return rows.filter(Boolean).join('\n');
}

function studentDisplayName(student) {
  return student?.name || student?.preferred_name || student?.email || student?.student_number || student?.id || 'Unnamed student';
}

function studentSearchText(student) {
  return [
    student?.name,
    student?.preferred_name,
    student?.email,
    student?.phone,
    student?.student_number,
    student?.application_number,
    student?.program_name,
    student?.program,
    student?.application_id,
    student?.id,
  ].filter(Boolean).join(' ').toLowerCase();
}

function isRegistrationNumberRequired(agencyType) {
  return agencyType === 'Domestic' || agencyType === 'International';
}

function requiredDocumentLabel(agencyType) {
  if (isRegistrationNumberRequired(agencyType)) return 'Company registration document';
  if (agencyType === 'Personal') return 'Government ID document';
  return NONE_PLACEHOLDER;
}

function formatFileSize(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isValidPercent(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= 100;
}

function safeContact(value) {
  return {
    name: value?.name || '',
    preferredName: value?.preferredName || '',
    title: value?.title || '',
    email: value?.email || '',
    phone: value?.phone || '',
  };
}

function safeMainContact(value) {
  return {
    ...safeContact(value),
    wechatId: value?.wechatId || '',
  };
}

function displayName(agent) {
  return agent?.displayName || agent?.preferredName || agent?.company_name || 'Unnamed agency';
}

function normalizeCountryCode(value) {
  const text = String(value || '').trim();
  const legacy = {
    Canada: 'CA',
    canada: 'CA',
    China: 'CN',
    china: 'CN',
    India: 'IN',
    india: 'IN',
    Philippines: 'PH',
    philippines: 'PH',
    Taiwan: 'TW',
    taiwan: 'TW',
    Vietnam: 'VN',
    vietnam: 'VN',
    'Hong Kong': 'HK',
    'United States': 'US',
  };
  return legacy[text] || text.toUpperCase();
}

function countryOptions(meta) {
  return safeList(meta.countries).filter((country) => country?.code);
}

function countryName(meta, code) {
  const normalized = countryCode(meta, code);
  const match = countryOptions(meta).find((country) => country.code === normalized);
  if (match) return match.name;
  if (normalized === 'INT') return 'International';
  return normalized || NONE_PLACEHOLDER;
}

function countryCode(meta, value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const normalized = normalizeCountryCode(text);
  const match = countryOptions(meta).find((country) => (
    String(country.code || '').toUpperCase() === normalized
    || String(country.name || '').toLocaleLowerCase('en-CA') === text.toLocaleLowerCase('en-CA')
  ));
  return match?.code || normalized;
}

function resolvedMarketCodes(form, meta) {
  const sourceMarkets = compactList(form.studentSourceCountries, 3)
    .map((country) => countryCode(meta, country));
  const storedPrimary = countryCode(meta, form.market);
  const storedSecondary = safeList(form.secondaryMarketRegions)
    .map((country) => countryCode(meta, country))
    .filter(Boolean);
  const fallbackMarkets = sourceMarkets;
  const chinaDefaultIsStale = storedPrimary === 'CN'
    && storedSecondary.length === 0
    && fallbackMarkets.length > 0
    && !fallbackMarkets.includes('CN');
  const useFallback = !storedPrimary || chinaDefaultIsStale;
  const primary = useFallback ? (fallbackMarkets[0] || '') : storedPrimary;
  const secondary = useFallback ? fallbackMarkets.slice(1) : storedSecondary;
  return [primary, ...secondary].filter((market, index, list) => market && list.indexOf(market) === index);
}

function defaultCommissionSchedule(agent) {
  const schedule = safeList(agent?.commissionSchedule)
    .map((row) => ({ year: String(row.year || ''), percent: String(row.percent ?? '') }))
    .filter((row) => row.year || row.percent);
  if (schedule.length) return schedule;
  const legacy = Number(agent?.commission_rate_first_year || 0);
  if (!Number.isFinite(legacy) || legacy <= 0) return [{ year: '1', percent: '' }];
  return [{ year: '1', percent: String(legacy <= 1 ? legacy * 100 : legacy) }];
}

function cleanCommissionSchedule(rows) {
  return safeList(rows)
    .map((row) => ({ year: Number(row.year), percent: Number(row.percent) }))
    .filter((row) => Number.isInteger(row.year) && row.year > 0 && Number.isFinite(row.percent) && row.percent >= 0 && row.percent <= 100);
}

const COMMISSION_YEAR_WORDS = [
  'First', 'Second', 'Third', 'Fourth', 'Fifth',
  'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth',
];

function commissionYearLabel(year) {
  const word = COMMISSION_YEAR_WORDS[year - 1];
  return word ? `${word} Year` : `Year ${year}`;
}

/* The agency rate card, read as the terms a commission payment can be filed
   under. Mirrors agent_commission_terms on the backend, which is authoritative. */
function commissionTermOptions(agent) {
  const seen = new Set();
  const terms = [];
  safeList(agent?.commissionSchedule)
    .map((row) => ({ year: Number(row.year), percent: Number(row.percent) }))
    .filter((row) => Number.isInteger(row.year) && row.year > 0 && Number.isFinite(row.percent))
    .sort((a, b) => a.year - b.year)
    .forEach((row) => {
      if (seen.has(row.year)) return;
      seen.add(row.year);
      terms.push({ key: `year:${row.year}`, label: commissionYearLabel(row.year), percent: row.percent });
    });
  const shortPercent = Number(agent?.shortProgramCommissionPercent ?? 20);
  terms.push({
    key: SHORT_PROGRAM_TERM,
    label: 'Short-term programs',
    percent: Number.isFinite(shortPercent) ? shortPercent : 20,
  });
  return terms;
}

function splitContactName(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
  };
}

function makePortalAccountForm(agent) {
  const mainContact = safeMainContact(agent?.mainContact);
  const nameParts = splitContactName(mainContact.name || agent?.agent_name || agent?.company_name);
  return {
    email: mainContact.email || agent?.email || '',
    first_name: nameParts.firstName,
    last_name: nameParts.lastName,
    password: '',
  };
}

/* Form plumbing */

function makeForm(agent) {
  const mainContact = safeMainContact(agent.mainContact);
  const secondaryContact = safeContact(agent.secondaryContact);
  const country = normalizeCountryCode(agent.country || 'CA');
  return {
    customFields: { ...(agent.customFields && typeof agent.customFields === 'object' ? agent.customFields : {}) },
    company_name: agent.company_name || '',
    preferredName: agent.preferredName || '',
    website: agent.website || '',
    agencyType: agent.agencyType || '',
    agent_name: agent.agent_name || '',
    sin: '',
    companyRegistrationNumber: agent.companyRegistrationNumber || '',
    contactInfo: agent.contactInfo || '',
    communicationChannels: safeList(agent.communicationChannels),
    communicationChannelOther: agent.communicationChannelOther || '',
    natureOfBusiness: agent.natureOfBusiness || '',
    natureOfBusinessOther: agent.natureOfBusinessOther || '',
    numberOfEmployees: agent.numberOfEmployees || '',
    address: agent.address || '',
    unitNumber: agent.unitNumber || '',
    city: agent.city || '',
    province: PROVINCE_STATE_COUNTRIES.has(country) ? fullRegionName(country, agent.province) : '',
    country,
    postalCode: agent.postalCode || '',
    gstNumber: agent.gstNumber || '',
    studentsSentToCanadaPast12Months: agent.studentsSentToCanadaPast12Months || '',
    studentsAnticipatedForMccNext12Months: agent.studentsAnticipatedForMccNext12Months || '',
    studentSourceCountries: safeList(agent.studentSourceCountries),
    targetEducationLevels: safeList(agent.targetEducationLevels),
    promotionalChannels: safeList(agent.promotionalChannels),
    howDidYouHearAboutMcc: agent.howDidYouHearAboutMcc || '',
    howDidYouHearAboutMccOther: agent.howDidYouHearAboutMccOther || '',
    email: agent.email || '',
    phone: agent.phone || '',
    mainContact,
    secondaryContact,
    showSecondaryContact: Object.values(secondaryContact).some((value) => String(value || '').trim()),
    createdBy: agent.createdBy || '',
    contractStartDate: (agent.contractStartDate || '').slice(0, 10),
    contractRenewalDate: (agent.contractRenewalDate || '').slice(0, 10),
    contractEndDate: (agent.contractEndDate || '').slice(0, 10),
    programsOfInterest: safeList(agent.programsOfInterest),
    internalRemarks: agent.internalRemarks || '',
    commissionSchedule: defaultCommissionSchedule(agent),
    shortProgramCommissionPercent: String(agent.shortProgramCommissionPercent ?? 20),
    tier: String(Number(agent.tier ?? 0)),
    status: agent.status || 'pending',
    market: normalizeCountryCode(agent.market),
    secondaryMarketRegions: safeList(agent.secondaryMarketRegions).map(normalizeCountryCode),
    secondaryMarketDraft: '',
    owner: agent.owner || UNASSIGNED,
    channel: agent.channel || '',
    firstContactDate: (agent.firstContactDate || '').slice(0, 10),
    lastContactDate: (agent.lastContactDate || '').slice(0, 10),
    followupDate: (agent.followupDate || '').slice(0, 10),
  };
}

function buildPatch(form, agent, meta) {
  const patch = {};
  const contactSummary = buildGeneralContactInfo(form);
  const [market = '', ...secondaryMarketRegions] = resolvedMarketCodes(form, meta);

  // Custom field values are a free-form map, so compare the whole object.
  const nextCustom = form.customFields && typeof form.customFields === 'object' ? form.customFields : {};
  const currentCustom = agent.customFields && typeof agent.customFields === 'object' ? agent.customFields : {};
  if (JSON.stringify(nextCustom) !== JSON.stringify(currentCustom)) {
    patch.customFields = nextCustom;
  }

  const strings = {
    company_name: form.company_name.trim(),
    preferredName: form.preferredName.trim(),
    website: form.website.trim(),
    agent_name: form.mainContact.name.trim() || form.agent_name.trim(),
    contactInfo: contactSummary || form.contactInfo.trim(),
    address: form.address.trim(),
    unitNumber: form.unitNumber.trim(),
    city: form.city.trim(),
    province: PROVINCE_STATE_COUNTRIES.has(normalizeCountryCode(form.country)) ? form.province.trim() : '',
    postalCode: form.postalCode.trim(),
    gstNumber: form.country === 'CA' ? form.gstNumber.trim() : '',
    email: form.email.trim(),
    phone: form.phone.trim(),
    channel: form.channel.trim(),
    status: form.status,
    market,
    createdBy: form.createdBy.trim(),
    internalRemarks: form.internalRemarks.trim(),
    communicationChannelOther: form.communicationChannelOther.trim(),
    natureOfBusiness: form.natureOfBusiness.trim(),
    natureOfBusinessOther: form.natureOfBusinessOther.trim(),
    numberOfEmployees: form.numberOfEmployees.trim(),
    studentsSentToCanadaPast12Months: form.studentsSentToCanadaPast12Months.trim(),
    studentsAnticipatedForMccNext12Months: form.studentsAnticipatedForMccNext12Months.trim(),
    howDidYouHearAboutMcc: form.howDidYouHearAboutMcc.trim(),
    howDidYouHearAboutMccOther: form.howDidYouHearAboutMccOther.trim(),
    companyRegistrationNumber: isRegistrationNumberRequired(form.agencyType) ? form.companyRegistrationNumber.trim() : '',
  };
  Object.entries(strings).forEach(([key, value]) => {
    if (value !== (agent[key] || '')) patch[key] = value;
  });

  const nextAgencyType = form.agencyType || null;
  if (nextAgencyType !== (agent.agencyType ?? null)) patch.agencyType = nextAgencyType;

  const nextCountry = form.country || null;
  if (nextCountry !== (agent.country ?? null)) patch.country = nextCountry;

  const nextSecondaryMarkets = [...secondaryMarketRegions].sort();
  const prevSecondaryMarkets = [...safeList(agent.secondaryMarketRegions).map(normalizeCountryCode)].sort();
  if (JSON.stringify(nextSecondaryMarkets) !== JSON.stringify(prevSecondaryMarkets)) {
    patch.secondaryMarketRegions = secondaryMarketRegions;
  }

  const nextMainContact = safeMainContact(form.mainContact);
  if (JSON.stringify(nextMainContact) !== JSON.stringify(safeMainContact(agent.mainContact))) {
    patch.mainContact = nextMainContact;
  }
  const nextSecondaryContact = safeContact(form.secondaryContact);
  if (JSON.stringify(nextSecondaryContact) !== JSON.stringify(safeContact(agent.secondaryContact))) {
    patch.secondaryContact = nextSecondaryContact;
  }

  if (form.agencyType === 'Personal' && form.sin.trim()) {
    patch.sin = form.sin.trim();
  }

  const nextTier = Number(form.tier);
  if (nextTier !== Number(agent.tier ?? 0)) patch.tier = nextTier;

  const nextSchedule = cleanCommissionSchedule(form.commissionSchedule);
  const prevSchedule = cleanCommissionSchedule(defaultCommissionSchedule(agent));
  if (JSON.stringify(nextSchedule) !== JSON.stringify(prevSchedule)) {
    patch.commissionSchedule = nextSchedule;
  }

  const nextShortProgramPercent = Number(form.shortProgramCommissionPercent || 20);
  const prevShortProgramPercent = Number(agent.shortProgramCommissionPercent ?? 20);
  if (nextShortProgramPercent !== prevShortProgramPercent) {
    patch.shortProgramCommissionPercent = nextShortProgramPercent;
  }

  const nextOwner = form.owner === UNASSIGNED ? null : form.owner;
  if (nextOwner !== (agent.owner ?? null)) patch.owner = nextOwner;

  [
    ['communicationChannels', compactList(form.communicationChannels)],
    ['studentSourceCountries', compactList(form.studentSourceCountries, 3)],
    ['targetEducationLevels', compactList(form.targetEducationLevels, 3)],
    ['programsOfInterest', compactList(form.programsOfInterest, 3)],
    ['promotionalChannels', compactList(form.promotionalChannels)],
  ].forEach(([key, nextValues]) => {
    const prevValues = compactList(agent[key], key === 'programsOfInterest' || key === 'studentSourceCountries' || key === 'targetEducationLevels' ? 3 : 100);
    if (JSON.stringify(nextValues) !== JSON.stringify(prevValues)) {
      patch[key] = nextValues;
    }
  });

  ['firstContactDate', 'lastContactDate', 'followupDate', 'contractStartDate', 'contractRenewalDate', 'contractEndDate'].forEach((key) => {
    const next = form[key] ? form[key] : null;
    if (next !== (agent[key] ?? null)) patch[key] = next;
  });

  return patch;
}

/* View sub-components */

function NotSet() {
  return <span className="font-normal text-[#062B1A]/40">{NONE_PLACEHOLDER}</span>;
}

function ListBadges({ values }) {
  const items = safeList(values).filter(Boolean);
  if (!items.length) return <NotSet />;
  return (
    <span className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <Badge key={item} variant="outline" className="border-[#062B1A]/12 bg-white/70 font-normal">
          {item}
        </Badge>
      ))}
    </span>
  );
}

function DocumentValue({ document }) {
  if (!document) return <NotSet />;
  const details = [
    formatFileSize(document.size),
    document.uploaded_at ? `Uploaded ${formatDate(document.uploaded_at)}` : '',
  ].filter(Boolean);
  return (
    <span className="space-y-1">
      <span className="block">{document.filename || document.file_name || document.doc_type || NONE_PLACEHOLDER}</span>
      {details.length > 0 && (
        <span className="block text-xs font-normal text-[#062B1A]/50">{details.join(' / ')}</span>
      )}
    </span>
  );
}

function ConfirmationBadge({ value }) {
  if (value !== true && value !== false) return <NotSet />;
  return (
    <Badge
      variant="outline"
      className={cn(
        'font-semibold',
        value
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-slate-200 bg-slate-50 text-slate-600',
      )}
    >
      {value ? 'Confirmed' : 'Not confirmed'}
    </Badge>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <CardTitle
      className="flex items-center gap-2 text-base text-[#062B1A]"
      style={{ fontFamily: 'var(--font-display)' }}
    >
      <Icon className="h-4 w-4 text-[#D4AF37]" /> {children}
    </CardTitle>
  );
}

function ProfileSection({ title, children }) {
  return (
    <section className="space-y-4">
      <h3
        className="text-base font-semibold text-[#062B1A]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">{children}</div>
    </section>
  );
}

class AgencyDetailErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Agency detail render failed', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => window.history.back()} className="-ml-2 text-[#062B1A]">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <Card className="border border-[rgba(185,28,28,0.18)] bg-red-50/70">
            <CardHeader>
              <CardTitle className="text-base text-red-800">Agency detail could not render</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-red-700">
              Refresh the page or return to the agency list. The page caught a bad field instead of showing a blank screen.
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Main */

function AdminAgencyDetailContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [agent, setAgent] = useState(null);
  const layoutController = useProfileLayout('agency_profile');
  const [meta, setMeta] = useState(DEFAULT_META);
  const [customFields, setCustomFields] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [linking, setLinking] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState(null);

  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [revealedSin, setRevealedSin] = useState('');
  const [revealingSin, setRevealingSin] = useState(false);
  const [commissionPayments, setCommissionPayments] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [commissionPaymentsLoading, setCommissionPaymentsLoading] = useState(false);
  const [commissionPaymentsError, setCommissionPaymentsError] = useState('');
  const [deletePaymentTarget, setDeletePaymentTarget] = useState(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);
  const [commissionChequeEditor, setCommissionChequeEditor] = useState(null);
  const [commissionRefreshToken, setCommissionRefreshToken] = useState(0);
  const [creatingChequePaymentId, setCreatingChequePaymentId] = useState('');
  const [portalAccountOpen, setPortalAccountOpen] = useState(false);
  const [portalAccountForm, setPortalAccountForm] = useState(makePortalAccountForm);
  const [creatingPortalAccount, setCreatingPortalAccount] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [documentInputKey, setDocumentInputKey] = useState(0);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState('');

  const refreshAgent = useCallback(async () => {
    const res = await getAgent(id);
    setAgent(res.data);
    return res.data;
  }, [id]);

  const refreshCommissionPayments = useCallback(async () => {
    setCommissionPaymentsLoading(true);
    try {
      const res = await getAgentCommissionPayments(id);
      setCommissionPayments(safeList(res.data?.items));
      setCommissionSummary(res.data?.summary || null);
      setCommissionPaymentsError('');
      return res.data;
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.detail || 'Could not load commission payments';
      setCommissionPaymentsError(message);
      toast.error(message);
      throw err;
    } finally {
      setCommissionPaymentsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const [agentResult, studentsResult, metaResult, fieldsResult] = await Promise.allSettled([
          getAgent(id),
          getStudents(),
          getAgentsMeta(),
          getAgencyFields(),
        ]);
        if (cancelled) return;

        if (agentResult.status === 'fulfilled') {
          setAgent(agentResult.value.data);
        } else if (agentResult.reason?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error(agentResult.reason);
          toast.error(agentResult.reason?.response?.data?.detail || 'Could not load agency');
        }

        if (fieldsResult.status === 'fulfilled') {
          setCustomFields(fieldsResult.value.data?.custom || []);
        }

        if (studentsResult.status === 'fulfilled') {
          setAllStudents(studentsResult.value.data || []);
        } else {
          console.error(studentsResult.reason);
          setAllStudents([]);
        }

        if (metaResult.status === 'fulfilled') {
          setMeta({ ...DEFAULT_META, ...(metaResult.value.data || {}) });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setCommissionPayments([]);
    setCommissionSummary(null);
    setCommissionPaymentsError('');
    setCommissionPaymentsLoading(true);
    (async () => {
      try {
        const res = await getAgentCommissionPayments(id);
        if (cancelled) return;
        setCommissionPayments(safeList(res.data?.items));
        setCommissionSummary(res.data?.summary || null);
      } catch (err) {
        if (!cancelled) {
          const message = err?.response?.data?.detail || 'Could not load commission payments';
          setCommissionPaymentsError(message);
          if (err?.response?.status !== 404) {
            toast.error(message);
          }
        }
      } finally {
        if (!cancelled) setCommissionPaymentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const linkedStudents = useMemo(() => safeList(agent?.students), [agent]);
  const linkedIds = useMemo(
    () => new Set(linkedStudents.map((student) => student.id)),
    [linkedStudents],
  );
  const linkedStudentNames = useMemo(
    () => new Map(linkedStudents.map((student) => [student.id, student.name || student.email || student.id])),
    [linkedStudents],
  );
  const commissionTerms = useMemo(() => commissionTermOptions(agent), [agent]);
  /* Students recorded together are one cheque, so the ledger shows them as one
     line rather than repeating the date, term and reference six times. */
  const commissionRows = useMemo(() => {
    const byKey = new Map();
    const ordered = [];
    commissionPayments.forEach((payment) => {
      const key = payment.batch_id || `single:${payment.id}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.push(payment);
        return;
      }
      const group = [payment];
      byKey.set(key, group);
      ordered.push({ key, payments: group });
    });
    return ordered.map(({ key, payments }) => {
      const [first] = payments;
      const names = [...new Set(payments
        .map((item) => item.student_name || linkedStudentNames.get(item.student_id) || '')
        .filter(Boolean))];
      const everyTuitionKnown = payments.every((item) => item.tuition_amount != null);
      return {
        ...first,
        key,
        payments,
        studentNames: names,
        commission_term_label: new Set(payments.map(item => item.commission_term_label)).size > 1 ? 'Mixed terms' : first.commission_term_label,
        mixedRates: new Set(payments.map(item => item.commission_percent)).size > 1,
        invoiceNumbers: payments.map(item => item.invoice_number).filter(Boolean),
        tuitionTotal: everyTuitionKnown
          ? payments.reduce((sum, item) => sum + Number(item.tuition_amount || 0), 0)
          : null,
        payoutTotal: payments.reduce((sum, item) => sum + Number(item.amount || 0), 0),
        cheque: payments.find((item) => item.cheque)?.cheque || null,
      };
    });
  }, [commissionPayments, linkedStudentNames]);

  const noteHistory = useMemo(
    () => safeList(agent?.noteHistory).map((note) => ({
      ...note,
      parentId: note.parentId || null,
      mentions: note.mentions || [],
      lastActivityAt: note.lastActivityAt || note.createdAt,
      sourceLabel: note.source_label || note.sourceLabel || '',
      sourceType: note.source_type || note.sourceType || '',
      sourceId: note.source_id || note.sourceId || '',
      sourceHref: note.source_href || note.sourceHref || '',
      ownerType: note.owner_type || note.ownerType || 'agency',
      ownerId: note.owner_id || note.ownerId || id,
    })),
    [agent, id],
  );
  const displayedCommissionSummary = commissionSummary || agent?.commission_summary || {
    total_paid: 0,
    total_paid_current_year: 0,
    count: 0,
  };

  const linkOptions = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    return allStudents
      .filter((student) => !linkedIds.has(student.id) && String(student.agent_id || '') !== id)
      .map((student) => ({
        ...student,
        isLinkedElsewhere: Boolean(student.agent_id && String(student.agent_id) !== id),
      }))
      .filter((student) => {
        if (!q) return true;
        return studentSearchText(student).includes(q);
      })
      .sort((left, right) => {
        if (left.isLinkedElsewhere !== right.isLinkedElsewhere) {
          return left.isLinkedElsewhere ? 1 : -1;
        }
        return studentDisplayName(left).localeCompare(studentDisplayName(right));
      })
      .slice(0, 50);
  }, [allStudents, id, linkedIds, studentSearch]);

  const openEdit = () => {
    setForm(makeForm(agent));
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditOpen(false);
    setForm(null);
  };

  const handleSave = async () => {
    if (!form || !agent) return;
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!form.country || !form.city.trim()) {
      toast.error('Company country and city are required');
      return;
    }
    const contactError = validateAgencyContactMethods({
      communicationChannels: form.communicationChannels,
      companyPhone: form.phone,
      companyEmail: form.email,
      mainContact: form.mainContact,
      secondaryContact: form.secondaryContact,
    });
    if (contactError) {
      toast.error(contactError);
      return;
    }
    if (!isValidWebsite(form.website)) {
      toast.error('Company website must start with http:// or https://');
      return;
    }
    if (form.sin.trim() && form.agencyType !== 'Personal') {
      toast.error('Set Type of agency to Personal to save a SIN number');
      return;
    }
    if (form.gstNumber.trim() && normalizeCountryCode(form.country) !== 'CA') {
      toast.error('GST number is only available for Canadian agencies');
      return;
    }
    if (!isValidPercent(form.shortProgramCommissionPercent)) {
      toast.error('Short programs percentage must be between 0 and 100');
      return;
    }
    const scheduleRows = cleanCommissionSchedule(form.commissionSchedule);
    const filledCommissionRows = safeList(form.commissionSchedule).filter((row) => String(row.year || row.percent || '').trim());
    if (scheduleRows.length !== filledCommissionRows.length) {
      toast.error('Commission rows need a positive year and a percentage from 0 to 100');
      return;
    }

    const patch = buildPatch(form, agent, meta);
    if (Object.keys(patch).length === 0) {
      toast.info('No changes to save');
      closeEdit();
      return;
    }

    try {
      setSaving(true);
      await patchAgent(id, patch);
      await refreshAgent();
      toast.success('Agency updated');
      setEditOpen(false);
      setForm(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not save agency');
    } finally {
      setSaving(false);
    }
  };

  const openPortalAccount = () => {
    setPortalAccountForm(makePortalAccountForm(agent));
    setPortalAccountOpen(true);
  };

  const closePortalAccount = () => {
    if (creatingPortalAccount) return;
    setPortalAccountOpen(false);
  };

  const setPortalAccountText = (key) => (event) => {
    setPortalAccountForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleCreatePortalAccount = async () => {
    const email = portalAccountForm.email.trim().toLowerCase();
    const firstName = portalAccountForm.first_name.trim();
    const lastName = portalAccountForm.last_name.trim();
    const password = portalAccountForm.password;
    if (!EMAIL_RE.test(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    if (!firstName) {
      toast.error('First name is required');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setCreatingPortalAccount(true);
      await createAgentPortalAccount(id, {
        email,
        first_name: firstName,
        last_name: lastName,
        password,
      });
      await refreshAgent();
      setPortalAccountOpen(false);
      setPortalAccountForm(makePortalAccountForm());
      toast.success('Agency portal account created');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not create agency portal account');
    } finally {
      setCreatingPortalAccount(false);
    }
  };

  const handleRevealSin = async () => {
    try {
      setRevealingSin(true);
      const res = await revealAgentSin(id);
      setRevealedSin(res.data?.sin || '');
      toast.success('SIN revealed');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not reveal SIN');
    } finally {
      setRevealingSin(false);
    }
  };

  /* Linked students */

  const handleLinkStudent = async () => {
    if (!selectedStudentId) {
      toast.error('Choose a student to link');
      return;
    }
    const selectedStudent = linkOptions.find((student) => student.id === selectedStudentId);
    if (!selectedStudent) {
      setSelectedStudentId('');
      toast.error('Choose a student shown in the current search');
      return;
    }
    if (selectedStudent?.isLinkedElsewhere) {
      toast.error('This student is linked to a different agency');
      return;
    }
    try {
      setLinking(true);
      await linkStudentToAgent(id, selectedStudentId);
      await refreshAgent();
      setSelectedStudentId('');
      setStudentSearch('');
      toast.success('Student linked');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not link student');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkStudent = async (student) => {
    const ok = window.confirm(`Unlink ${student.name || student.email || 'this student'} from this agency?`);
    if (!ok) return;
    try {
      setUnlinkingId(student.id);
      await unlinkStudentFromAgent(id, student.id);
      await refreshAgent();
      toast.success('Student unlinked');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not unlink student');
    } finally {
      setUnlinkingId(null);
    }
  };

  /* Commission payments */

  const handleDeleteCommissionPayment = async () => {
    if (!deletePaymentTarget) return;
    const batchId = deletePaymentTarget.batch_id;
    const count = deletePaymentTarget.payments?.length || 1;
    try {
      setDeletingPaymentId(deletePaymentTarget.key || deletePaymentTarget.id);
      if (batchId && count > 1) await deleteCommissionPaymentBatch(batchId);
      else await deleteCommissionPayment(deletePaymentTarget.id);
      await Promise.all([refreshCommissionPayments(), refreshAgent()]);
      setDeletePaymentTarget(null);
      toast.success('Commission payment deleted');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete commission payment');
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const handleCreateCommissionCheque = async (payment) => {
    if (payment.cheque) {
      setCommissionChequeEditor(payment.cheque);
      return;
    }
    /* Students recorded together were always meant for one cheque. */
    const paymentIds = (payment.payments || [payment]).filter((item) => !item.cheque).map((item) => item.id);
    try {
      setCreatingChequePaymentId(payment.key || payment.id);
      const response = await createAgentCommissionCheque(id, paymentIds.length ? paymentIds : [payment.id]);
      setCommissionChequeEditor(response.data);
      toast.success('Commission cheque prepared from the agency and payment information.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Commission cheque could not be prepared.');
    } finally {
      setCreatingChequePaymentId('');
    }
  };

  /* Notes */

  const handleEditNote = async (note, text) => {
    try {
      const res = await updateAgentNote(id, note.id, { text });
      setAgent((prev) => ({
        ...prev,
        noteHistory: safeList(prev?.noteHistory).map((item) => (
          item.id === note.id ? res.data : item
        )),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not update the note');
      throw err;
    }
  };

  const handleAddNote = async (rawText, parentId) => {
    const text = String(rawText || '').trim();
    if (!text) {
      toast.error('Write a note first');
      return;
    }
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      text,
      parentId: parentId || null,
      mentions: parseMentions(text),
      author: user?.name || user?.email || 'You',
      createdAt: new Date().toISOString(),
      lastActivityAt: new Date().toISOString(),
      sourceLabel: 'Agency profile',
      sourceType: 'agency',
      sourceId: id,
      ownerType: 'agency',
      ownerId: id,
      _pending: true,
    };
    setAgent((prev) => {
      const history = [optimistic, ...safeList(prev?.noteHistory)];
      // Mirror the server-side bump so the replied-to thread rises immediately.
      return {
        ...prev,
        noteHistory: parentId
          ? history.map((note) => (
            note.id === parentId ? { ...note, lastActivityAt: optimistic.createdAt } : note
          ))
          : history,
      };
    });
    setAddingNote(true);
    try {
      const res = await addAgentNote(id, {
        text,
        parentId: parentId || null,
        source_type: 'agency',
        source_id: id,
      });
      setAgent((prev) => ({
        ...prev,
        noteHistory: safeList(prev?.noteHistory).map((note) => (note.id === tempId ? res.data : note)),
      }));
    } catch (err) {
      console.error(err);
      setAgent((prev) => ({
        ...prev,
        noteHistory: safeList(prev?.noteHistory).filter((note) => note.id !== tempId),
      }));
      setNoteText(text);
      toast.error(err?.response?.data?.detail || 'Could not add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (note) => {
    if (note._pending) return;
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    const previousHistory = safeList(agent?.noteHistory);
    setDeletingNoteId(note.id);
    setAgent((prev) => ({
      ...prev,
      noteHistory: notesAfterDelete(prev?.noteHistory, note.id),
    }));
    try {
      await deleteAgentNote(id, note.id);
      toast.success('Note deleted');
    } catch (err) {
      console.error(err);
      setAgent((prev) => ({ ...prev, noteHistory: previousHistory }));
      toast.error(err?.response?.data?.detail || 'Could not delete note');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleUploadDocument = async (event) => {
    event.preventDefault();
    const title = documentTitle.trim();
    if (!title || !documentFile) {
      toast.error('Add a document title and choose a file');
      return;
    }
    const data = new FormData();
    data.append('title', title);
    data.append('file', documentFile);
    setUploadingDocument(true);
    try {
      const res = await uploadAgentDocument(id, data);
      setAgent((prev) => ({
        ...prev,
        manualDocuments: [...safeList(prev?.manualDocuments), res.data],
      }));
      setDocumentTitle('');
      setDocumentFile(null);
      setDocumentInputKey((value) => value + 1);
      toast.success('Agency document uploaded');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not upload the document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDownloadDocument = async (document) => {
    try {
      const res = await getAgentDocumentDownload(id, document.id);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not prepare the download');
    }
  };

  const handleDeleteDocument = async (document) => {
    if (!window.confirm(`Remove "${document.title || document.filename}" from this agency?`)) return;
    setDeletingDocumentId(document.id);
    try {
      await deleteAgentDocument(id, document.id);
      setAgent((prev) => ({
        ...prev,
        manualDocuments: safeList(prev?.manualDocuments).filter((item) => item.id !== document.id),
      }));
      toast.success('Agency document removed');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not remove the document');
    } finally {
      setDeletingDocumentId('');
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteAgent(id);
      toast.success('Agency deleted');
      navigate('/admin/agencies');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete agency');
      setDeleting(false);
    }
  };

  /* Loading / not-found */

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-40 animate-pulse rounded-lg bg-[#F7F8FA]" />
        ))}
      </div>
    );
  }

  if (notFound || !agent) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/admin/agencies')} className="-ml-2 text-[#062B1A]">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to agencies
        </Button>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <Building2 className="mb-3 size-12 text-[#062B1A]/15" />
            <h3 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Agency not found
            </h3>
            <p className="mt-2 max-w-md text-sm text-[#062B1A]/50">
              This agency may have been deleted, or the link is out of date.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentTier = Number(agent.tier ?? 0);
  const studentCount = linkedStudents.length;
  const followupOverdue = isOverdue(agent.followupDate);
  const programs = safeList(agent.programsOfInterest);
  const mainContact = safeMainContact(agent.mainContact);
  const secondaryContact = safeContact(agent.secondaryContact);
  const agencyName = displayName(agent);
  const documentRequirement = requiredDocumentLabel(agent.agencyType);
  const agentDocuments = safeList(agent.documents);
  const companyRegistrationDocument = agentDocuments.find((document) => document?.doc_type === 'company_registration');
  const governmentIdDocument = agentDocuments.find((document) => document?.doc_type === 'government_id');
  const manualDocuments = safeList(agent.manualDocuments);
  const publicApplication = agent.publicApplication || {};
  const portalAccount = agent.portalAccount || {};

  /* Render */

  return (
    <div className="space-y-6 animate-fade-in">
      {/* top bar */}
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => navigate('/admin/agencies')} className="-ml-2 text-[#062B1A]">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to agencies
        </Button>
        <div className="flex items-center gap-2">
          <PortalEmailComposer
            audience="agencies"
            recipients={[agent]}
            buttonLabel="Email agency"
            buttonClassName="border-[#D4AF37]/45 text-[#7A5A05]"
          />
          <Button
            variant="outline"
            onClick={() => setDeleteOpen(true)}
            className="border-[rgba(220,38,38,0.28)] text-[rgb(153,27,27)] hover:bg-[rgba(220,38,38,0.06)]"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </Button>
          <ProfileLayoutToolbar controller={layoutController} />
          <Button onClick={openEdit} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
            <PencilLine className="mr-2 h-4 w-4" /> Edit agency
          </Button>
        </div>
      </div>

      {/* Profile header */}
      <ProfileHero
        avatar={(
          <Avatar className="h-24 w-24 border-4 border-white shadow-md">
            <AvatarFallback className="bg-[#D4AF37] text-2xl font-bold text-[#062B1A]">
              {agencyInitials(agencyName)}
            </AvatarFallback>
          </Avatar>
        )}
      >
        <h2
          className="break-words text-2xl font-semibold text-white"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {agencyName}
        </h2>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90">
          <span className="inline-flex items-center gap-2">
            <UserRound className="h-4 w-4" />
            {mainContact.name || agent.agent_name || 'No contact named'}
          </span>
          <span>{studentCount} linked student{studentCount === 1 ? '' : 's'}</span>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Badge variant="outline" className="max-w-full flex-wrap gap-1.5 whitespace-normal border-white/40 bg-white py-1 font-semibold text-[#062B1A]">
            <span>Tier {currentTier}</span>
            <span className="font-normal">{tierLabel(currentTier, meta.tierLabels)}</span>
          </Badge>
          <Badge variant="outline" className="border-white/40 bg-white font-semibold capitalize text-[#062B1A]">
            {labelize(agent.status)}
          </Badge>
          <Badge variant="outline" className="border-[#D4AF37]/65 bg-[#FFF7D6] font-semibold text-[#6B4E00]">
            Paid {formatMoney(displayedCommissionSummary.total_paid)}
          </Badge>
        </div>
      </ProfileHero>

      <AgencyDashboardEditor agentId={agent.id || id} />

      {/* profile sections */}
      <ProfileSection title="1. Agency Contact Information">
        <LayoutSection id="company_identity" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={Building2}>Company identity</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={Building2} label="Company legal name">
              {present(agent.company_name) ? agent.company_name : <NotSet />}
            </InfoRow>
            <InfoRow icon={UserRound} label="Company preferred/brand name">
              {present(agent.preferredName) ? agent.preferredName : <NotSet />}
            </InfoRow>
            <InfoRow icon={Phone} label="Company phone number">
              {present(agent.phone) ? agent.phone : <NotSet />}
            </InfoRow>
            <InfoRow icon={Mail} label="Company email address">
              {present(agent.email) ? agent.email : <NotSet />}
            </InfoRow>
            <InfoRow icon={Globe2} label="Company website">
              {present(agent.website) ? agent.website : <NotSet />}
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="company_address" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={MapPin}>Company Address</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={Hash} label="Apt/Unit/Suite #">
              {present(agent.unitNumber) ? agent.unitNumber : <NotSet />}
            </InfoRow>
            <InfoRow icon={MapPin} label="Street Address">
              {present(agent.address) ? agent.address : <NotSet />}
            </InfoRow>
            <InfoRow icon={Building2} label="City">
              {present(agent.city) ? agent.city : <NotSet />}
            </InfoRow>
            <InfoRow icon={Globe2} label="Country">
              {present(agent.country) ? countryName(meta, agent.country) : <NotSet />}
            </InfoRow>
            <InfoRow icon={Compass} label="Province/State">
              {present(agent.province) ? fullRegionName(agent.country, agent.province) : <NotSet />}
            </InfoRow>
            <InfoRow icon={Hash} label="Postal Code">
              {present(agent.postalCode) ? agent.postalCode : <NotSet />}
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="main_contact" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={UserRound}>Main Contact Person</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={UserRound} label="Full name">
              {present(mainContact.name) ? mainContact.name : (present(agent.agent_name) ? agent.agent_name : <NotSet />)}
            </InfoRow>
            <InfoRow icon={UserRound} label="Preferred name">
              {present(mainContact.preferredName) ? mainContact.preferredName : <NotSet />}
            </InfoRow>
            <InfoRow icon={Handshake} label="Job title">
              {present(mainContact.title) ? mainContact.title : <NotSet />}
            </InfoRow>
            <InfoRow icon={Phone} label="Phone number">
              {present(mainContact.phone) ? mainContact.phone : (present(agent.phone) ? agent.phone : <NotSet />)}
            </InfoRow>
            <InfoRow icon={Mail} label="Email address">
              {present(mainContact.email) ? mainContact.email : (present(agent.email) ? agent.email : <NotSet />)}
            </InfoRow>
            <InfoRow icon={Compass} label="WeChat ID">
              {present(mainContact.wechatId) ? mainContact.wechatId : <NotSet />}
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="secondary_contact" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={Users}>Secondary Contact Person</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={UserRound} label="Full name">
              {present(secondaryContact.name) ? secondaryContact.name : <NotSet />}
            </InfoRow>
            <InfoRow icon={UserRound} label="Preferred name">
              {present(secondaryContact.preferredName) ? secondaryContact.preferredName : <NotSet />}
            </InfoRow>
            <InfoRow icon={Handshake} label="Job title">
              {present(secondaryContact.title) ? secondaryContact.title : <NotSet />}
            </InfoRow>
            <InfoRow icon={Phone} label="Phone number">
              {present(secondaryContact.phone) ? secondaryContact.phone : <NotSet />}
            </InfoRow>
            <InfoRow icon={Mail} label="Email address">
              {present(secondaryContact.email) ? secondaryContact.email : <NotSet />}
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="communication_channels" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={Compass}>Preferred Channel of Communication</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={Compass} label="Preferred Channel of Communication">
              <ListBadges values={agent.communicationChannels} />
            </InfoRow>
            {present(agent.communicationChannelOther) && (
              <InfoRow icon={Compass} label="Other communication channel">
                {agent.communicationChannelOther}
              </InfoRow>
            )}
          </CardContent>
        </Card>
        </LayoutSection>
      </ProfileSection>

      <ProfileSection title="2. Agency Business Information">
        <LayoutSection id="business_info" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={Target}>Agency Business Information</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={Handshake} label="Nature of business">
              {present(agent.natureOfBusiness) ? agent.natureOfBusiness : <NotSet />}
            </InfoRow>
            {present(agent.natureOfBusinessOther) && (
              <InfoRow icon={Handshake} label="Other nature of business">
                {agent.natureOfBusinessOther}
              </InfoRow>
            )}
            <InfoRow icon={Users} label="Number of Employees">
              {present(agent.numberOfEmployees) ? agent.numberOfEmployees : <NotSet />}
            </InfoRow>
            <InfoRow icon={Hash} label="Company Registration Number">
              {present(agent.companyRegistrationNumber) ? agent.companyRegistrationNumber : <NotSet />}
            </InfoRow>
            {(normalizeCountryCode(agent.country) === 'CA' || present(agent.gstNumber)) && (
              <InfoRow icon={Hash} label="GST Number (if applicable)">
                {present(agent.gstNumber) ? agent.gstNumber : <NotSet />}
              </InfoRow>
            )}
            {(agent.agencyType === 'Personal' || agent.sinMask || revealedSin) && (
              <InfoRow icon={Hash} label="SIN Number (if applicable)">
                <span className="flex flex-wrap items-center gap-2">
                  <span>{revealedSin || agent.sinMask || NONE_PLACEHOLDER}</span>
                  {agent.sinMask && !revealedSin && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleRevealSin}
                      disabled={revealingSin}
                      className="h-7 border-[rgba(6,43,26,0.16)] px-2 text-xs text-[#062B1A]"
                    >
                      {revealingSin ? 'Revealing...' : 'Reveal'}
                    </Button>
                  )}
                </span>
              </InfoRow>
            )}
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="market_details" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={Globe2}>Student market details</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={Users} label="Number of students sent to Canada in the past 12 months">
              {present(agent.studentsSentToCanadaPast12Months) ? agent.studentsSentToCanadaPast12Months : <NotSet />}
            </InfoRow>
            <InfoRow icon={Users} label="Number of students anticipated for MCC in the next 12 months">
              {present(agent.studentsAnticipatedForMccNext12Months) ? agent.studentsAnticipatedForMccNext12Months : <NotSet />}
            </InfoRow>
            <InfoRow icon={Globe2} label="Top 3 countries your students come from">
              <ListBadges values={agent.studentSourceCountries} />
            </InfoRow>
            <InfoRow icon={Target} label="Top 3 target education levels your students seek">
              <ListBadges values={agent.targetEducationLevels} />
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="program_promotion" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={ShoppingBag}>Program and promotion</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={ShoppingBag} label="Top programs of interest">
              {programs.length ? (
                <span className="flex flex-wrap gap-1.5">
                  {programs.map((program, index) => (
                    <Badge
                      key={`${program}-${index}`}
                      variant="outline"
                      title={program}
                      aria-label={`${index + 1}. ${program}`}
                      className="border-[#062B1A]/12 bg-white/70 font-normal"
                    >
                      {index + 1}. {agencyProgramAbbreviation(program, meta)}
                    </Badge>
                  ))}
                </span>
              ) : (
                <NotSet />
              )}
            </InfoRow>
            <InfoRow icon={Send} label="Promotional channel(s) used in the past 12 months">
              <ListBadges values={agent.promotionalChannels} />
            </InfoRow>
            <InfoRow icon={Compass} label="How did you hear about MCC?">
              {present(agent.howDidYouHearAboutMcc) ? agent.howDidYouHearAboutMcc : <NotSet />}
            </InfoRow>
            {present(agent.howDidYouHearAboutMccOther) && (
              <InfoRow icon={Compass} label="Other source">
                {agent.howDidYouHearAboutMccOther}
              </InfoRow>
            )}
          </CardContent>
        </Card>
        </LayoutSection>
      </ProfileSection>

      <ProfileSection title="3. Upload Documents">
        <LayoutSection id="upload_documents" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={FileText}>Upload Documents</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={FileText} label="Company registration document">
              <DocumentValue document={companyRegistrationDocument} />
            </InfoRow>
            <InfoRow icon={FileText} label="Government ID document">
              <DocumentValue document={governmentIdDocument} />
            </InfoRow>
            {present(agent.agencyType) && (
              <InfoRow icon={StickyNote} label="Required Document">
                {documentRequirement}
              </InfoRow>
            )}
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="contracts_documents" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={FileText}>Agency contracts &amp; documents</SectionTitle>
            <p className="text-sm text-[#062B1A]/55">
              Manually attach contracts, amendments, compliance files, or other agency records.
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            <form onSubmit={handleUploadDocument} className="grid gap-3 rounded-lg border border-dashed border-[#062B1A]/20 bg-[#F7F8FA] p-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="agency-document-title">Document title</Label>
                <Input
                  id="agency-document-title"
                  value={documentTitle}
                  onChange={(event) => setDocumentTitle(event.target.value)}
                  maxLength={120}
                  placeholder="e.g. 2026 Recruitment Contract"
                  className={inputClass}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="agency-document-file">File</Label>
                <Input
                  key={documentInputKey}
                  id="agency-document-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}
                  className={inputClass}
                />
              </div>
              <Button type="submit" disabled={uploadingDocument || !documentTitle.trim() || !documentFile} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                {uploadingDocument ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                {uploadingDocument ? 'Uploading...' : 'Upload'}
              </Button>
            </form>

            {manualDocuments.length ? (
              <div className="divide-y divide-[#062B1A]/8 rounded-lg border border-[#062B1A]/10">
                {manualDocuments.map((document) => (
                  <div key={document.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-[#062B1A]">{document.title || document.filename}</p>
                      <p className="mt-1 text-xs text-[#062B1A]/50">
                        {[document.filename, formatFileSize(document.size), document.uploaded_at ? `Uploaded ${formatDate(document.uploaded_at)}` : '', document.uploaded_by ? `by ${document.uploaded_by}` : ''].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => handleDownloadDocument(document)}>
                        <Download className="mr-2 h-4 w-4" /> Download
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={deletingDocumentId === document.id}
                        onClick={() => handleDeleteDocument(document)}
                        className="border-red-200 text-red-700 hover:bg-red-50"
                      >
                        {deletingDocumentId === document.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-[#062B1A]/8 bg-white p-5 text-center text-sm text-[#062B1A]/45">
                No contracts or additional documents have been added yet.
              </p>
            )}
          </CardContent>
        </Card>
        </LayoutSection>
      </ProfileSection>

      <ProfileSection title="4. Consent and Privacy">
        <LayoutSection id="consent_privacy" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={StickyNote}>Consent and Privacy</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={StickyNote} label="Consent confirmation">
              <ConfirmationBadge value={publicApplication.consentConfirmed} />
            </InfoRow>
            <InfoRow icon={StickyNote} label="Privacy confirmation">
              <ConfirmationBadge value={publicApplication.privacyConfirmed} />
            </InfoRow>
            <InfoRow icon={CalendarClock} label="Submitted at">
              {present(publicApplication.submittedAt) ? formatDateTime(publicApplication.submittedAt) : <NotSet />}
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>
      </ProfileSection>

      <ProfileSection title="5. Portal Management">
        <LayoutSection id="portal_status" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={CalendarClock}>Portal status</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={Handshake} label="Type of agency">
              {present(agent.agencyType) ? agent.agencyType : <NotSet />}
            </InfoRow>
            <InfoRow icon={Target} label="Tier of Agency">
              <Badge variant="outline" className={cn('font-semibold', TIER_BADGE_STYLES[currentTier] || TIER_BADGE_STYLES[0])}>
                Tier {currentTier}
              </Badge>
              <span className="ml-2 text-[#062B1A]/55 font-normal">{tierLabel(currentTier, meta.tierLabels)}</span>
            </InfoRow>
            <InfoRow icon={CalendarClock} label="Status">
              <Badge variant="outline" className={cn('font-semibold', STATUS_STYLES[agent.status] || STATUS_STYLES.new)}>
                {labelize(agent.status)}
              </Badge>
            </InfoRow>
            <InfoRow icon={KeyRound} label="Agency portal account">
              {portalAccount.status === 'active' ? (
                <span className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                    Active
                  </Badge>
                  <span className="text-[#062B1A]/65">{portalAccount.email || 'Account linked'}</span>
                </span>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  onClick={openPortalAccount}
                  className="h-8 bg-[#062B1A] px-3 text-xs text-white hover:bg-[#041B10]"
                >
                  <KeyRound className="mr-2 h-3.5 w-3.5" />
                  Create portal account
                </Button>
              )}
            </InfoRow>
            <InfoRow icon={UserRound} label="MCC Representative">
              {present(agent.owner) ? agent.owner : <NotSet />}
            </InfoRow>
            <InfoRow icon={UserRound} label="Created by">
              {present(agent.createdBy) ? agent.createdBy : <NotSet />}
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="markets_followup" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={Globe2}>Markets and follow-up</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={Compass} label="Preferred Contact Channel">
              {present(agent.channel) ? agent.channel : <NotSet />}
            </InfoRow>
            <InfoRow icon={CalendarDays} label="1st Contact Date">
              {present(agent.firstContactDate) ? formatDate(agent.firstContactDate) : <NotSet />}
            </InfoRow>
            <InfoRow icon={CalendarDays} label="Last Contact Date">
              {present(agent.lastContactDate) ? formatDate(agent.lastContactDate) : <NotSet />}
            </InfoRow>
            <InfoRow icon={CalendarClock} label="Next Follow Up Date">
              {present(agent.followupDate) ? (
                <span className={cn('inline-flex items-center gap-1.5', followupOverdue && 'text-red-700')}>
                  {formatDate(agent.followupDate)}
                  {followupOverdue && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                      <AlertTriangle className="h-3 w-3" /> Overdue
                    </span>
                  )}
                </span>
              ) : (
                <NotSet />
              )}
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>
      </ProfileSection>

      <ProfileSection title="6. Agreement and Commission">
        <AgencyAgreementCard mode="admin" agentId={id} className="lg:col-span-12" />

        <LayoutSection id="agreement_details" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={CalendarClock}>Agreement details</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={CalendarDays} label="Contract Start Date">
              {present(agent.contractStartDate) ? formatDate(agent.contractStartDate) : <NotSet />}
            </InfoRow>
            <InfoRow icon={CalendarClock} label="Contract Renewal Date">
              {present(agent.contractRenewalDate) ? formatDate(agent.contractRenewalDate) : <NotSet />}
            </InfoRow>
            <InfoRow icon={CalendarDays} label="Contract End Date">
              {present(agent.contractEndDate) ? formatDate(agent.contractEndDate) : <NotSet />}
            </InfoRow>
            <InfoRow icon={StickyNote} label="Internal Remarks">
              {present(agent.internalRemarks) ? agent.internalRemarks : <NotSet />}
            </InfoRow>
          </CardContent>
        </Card>
        </LayoutSection>

        <LayoutSection id="commission_students" controller={layoutController}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <SectionTitle icon={Handshake}>Commission and linked students</SectionTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* One rate card: tuition years and short-term programs read as a
                single table even though they are stored apart. */}
            <div className="overflow-hidden rounded-lg border border-[rgba(6,43,26,0.08)]">
              <div className="grid grid-cols-2 bg-[#F7F8FA] px-3 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/50">
                <span>Scope</span>
                <span>Percentage</span>
              </div>
              {safeList(agent.commissionSchedule).length ? (
                safeList(agent.commissionSchedule).map((row, index) => (
                  <div key={`${row.year}-${index}`} className="grid grid-cols-2 border-t border-[rgba(6,43,26,0.06)] px-3 py-2 text-sm text-[#062B1A]">
                    <span>Year {row.year}</span>
                    <span className="font-semibold">{formatPercentNumber(row.percent)}</span>
                  </div>
                ))
              ) : (
                <div className="border-t border-[rgba(6,43,26,0.06)] px-3 py-4 text-sm text-[#062B1A]/45">
                  No tuition-year commission set.
                </div>
              )}
              <div className="grid grid-cols-2 border-t border-[rgba(6,43,26,0.06)] bg-[#F7F8FA]/60 px-3 py-2 text-sm text-[#062B1A]">
                <span>Short-term programs</span>
                <span className="font-semibold">{formatPercentNumber(agent.shortProgramCommissionPercent ?? 20)}</span>
              </div>
            </div>

            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-[#062B1A]">
                <Users className="h-4 w-4 text-[#D4AF37]" /> Linked students ({studentCount})
              </p>
              {linkedStudents.length === 0 ? (
                <p className="text-sm text-[#062B1A]/45">No students linked to this agency.</p>
              ) : (
                <div className="divide-y divide-[rgba(6,43,26,0.06)] overflow-hidden rounded-lg border border-[rgba(6,43,26,0.08)]">
                  {linkedStudents.map((student) => (
                    <div key={student.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-[#062B1A]">{student.name || 'Unnamed student'}</p>
                        <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[#062B1A]/55">
                          <span className="truncate">{student.email || 'No email'}</span>
                          <span>{formatMoney(student.tuition_paid)} paid of {formatMoney(student.tuition_total)}</span>
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnlinkStudent(student)}
                        disabled={unlinkingId === student.id}
                        className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                      >
                        <Unlink className="mr-2 h-4 w-4" />
                        {unlinkingId === student.id ? 'Unlinking...' : 'Unlink'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA]/60 p-3">
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
                  <LinkIcon className="h-3.5 w-3.5 text-[#D4AF37]" /> Link a student
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    type="search"
                    aria-label="Search students to link"
                    value={studentSearch}
                    onChange={(event) => {
                      setStudentSearch(event.target.value);
                      setSelectedStudentId('');
                    }}
                    placeholder="Search name, email, number, or program..."
                    className={cn('sm:max-w-[220px]', inputClass)}
                  />
                  <Select value={selectedStudentId || undefined} onValueChange={setSelectedStudentId}>
                    <SelectTrigger className={cn('flex-1', inputClass)}>
                      <SelectValue placeholder="Choose a student" />
                    </SelectTrigger>
                    <SelectContent>
                      {linkOptions.map((student) => (
                        <SelectItem
                          key={student.id}
                          value={student.id}
                          disabled={student.isLinkedElsewhere}
                          textValue={`${studentDisplayName(student)} ${student.email || ''} ${student.isLinkedElsewhere ? 'Linked to another agency' : ''}`}
                        >
                          <div className={cn('flex flex-col', student.isLinkedElsewhere && 'text-[#062B1A]/45')}>
                            <span>{studentDisplayName(student)}</span>
                            <span className="text-xs text-[#062B1A]/45">
                              {student.isLinkedElsewhere ? 'Linked to another agency' : (student.email || student.student_number || student.id)}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleLinkStudent}
                    disabled={linking || !selectedStudentId}
                    className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    {linking ? 'Linking...' : 'Link'}
                  </Button>
                </div>
                {linkOptions.length === 0 && (
                  <p className="mt-2 text-xs text-[#062B1A]/50">No available students match this search.</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        </LayoutSection>
      </ProfileSection>

      <ProfileSectionGrid>
      {/* Commission payments */}
      <LayoutSection id="commission_payments" controller={layoutController}>
      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <SectionTitle icon={CreditCard}>Commission Payments</SectionTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA]/70 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">Total paid</p>
              <p className="mt-1 text-lg font-semibold text-[#062B1A]">{formatMoney(displayedCommissionSummary.total_paid)}</p>
            </div>
            <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA]/70 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">This year</p>
              <p className="mt-1 text-lg font-semibold text-[#062B1A]">{formatMoney(displayedCommissionSummary.total_paid_current_year)}</p>
            </div>
            <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA]/70 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45"># payments</p>
              <p className="mt-1 text-lg font-semibold text-[#062B1A]">{displayedCommissionSummary.count || 0}</p>
            </div>
          </div>

          <CommissionChequeBuilder key={id} agentId={id} students={linkedStudents} terms={commissionTerms} refreshToken={commissionRefreshToken} onCreated={async (cheque) => {
            setCommissionChequeEditor(cheque);
            await Promise.all([refreshCommissionPayments(), refreshAgent()]);
          }} />

          {commissionPaymentsError && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {commissionPaymentsError}
            </p>
          )}

          <div className="overflow-x-auto rounded-lg border border-[rgba(6,43,26,0.08)]">
            <table className="min-w-[1320px] w-full text-left text-sm">
              <thead className="bg-[#F7F8FA] text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/50">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Students</th>
                  <th className="px-3 py-2">Term</th>
                  <th className="px-3 py-2">Commission base</th>
                  <th className="px-3 py-2">Rate</th>
                  <th className="px-3 py-2">Payout</th>
                  <th className="px-3 py-2">Method</th>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Note</th>
                  <th className="px-3 py-2">Recorded by</th>
                  <th className="px-3 py-2">Cheque status</th>
                  <th className="px-3 py-2 text-right">Delete</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(6,43,26,0.06)] bg-white/70">
                {commissionPaymentsLoading && commissionRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-3 py-5 text-center text-sm text-[#062B1A]/45">
                      Loading commission payments...
                    </td>
                  </tr>
                ) : commissionRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-3 py-5 text-center text-sm text-[#062B1A]/45">
                      No commission payments recorded
                    </td>
                  </tr>
                ) : (
                  commissionRows.map((payment) => {
                    const batchSize = payment.payments.length;
                    const allNames = payment.studentNames.join(', ');
                    return (
                      <tr key={payment.key} className="text-[#062B1A]">
                        <td className="whitespace-nowrap px-3 py-2 align-top">{formatDate(payment.payment_date)}</td>
                        <td className="max-w-[240px] px-3 py-2 align-top" title={allNames}>
                          {batchSize > 1 ? (
                            <div>
                              <p className="font-semibold">{payment.studentNames.length} students · {batchSize} lines</p>
                              <p className="text-xs text-[#062B1A]/50">{allNames}</p><p className="text-xs text-[#062B1A]/50">{payment.invoiceNumbers.join(', ')}</p>
                            </div>
                          ) : (
                            <span className="block truncate">{payment.studentNames[0] || 'Not set'}</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 align-top">{payment.commission_term_label || 'Not set'}</td>
                        <td className="whitespace-nowrap px-3 py-2 align-top">
                          {payment.tuitionTotal == null ? 'Not set' : formatMoney(payment.tuitionTotal)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 align-top">
                          {payment.mixedRates ? 'Mixed rates' : payment.commission_percent == null ? 'Not set' : formatPercentNumber(payment.commission_percent)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 align-top font-semibold">{formatMoney(payment.payoutTotal)}</td>
                        <td className="max-w-[140px] truncate px-3 py-2 align-top" title={payment.method || ''}>{payment.method || 'Not set'}</td>
                        <td className="max-w-[150px] truncate px-3 py-2 align-top" title={payment.reference || ''}>{payment.reference || 'Not set'}</td>
                        <td className="max-w-[220px] truncate px-3 py-2 align-top" title={payment.note || ''}>{payment.note || 'Not set'}</td>
                        <td className="max-w-[160px] truncate px-3 py-2 align-top" title={payment.created_by_name || ''}>
                          {payment.created_by_name || 'Not set'}
                        </td>
                        <td className="px-3 py-2">
                          {payment.cheque ? (
                            <div>
                              <Badge
                                variant="outline"
                                className={cn(
                                  payment.cheque.status === 'issued' && 'border-blue-200 bg-blue-50 text-blue-700',
                                  payment.cheque.status === 'cleared' && 'border-emerald-200 bg-emerald-50 text-emerald-700',
                                  payment.cheque.status === 'void' && 'border-red-200 bg-red-50 text-red-700',
                                  (!payment.cheque.status || payment.cheque.status === 'draft') && 'border-slate-200 bg-slate-100 text-slate-700',
                                )}
                              >
                                {(payment.cheque.status || 'draft').toUpperCase()}
                              </Badge>
                              {payment.cheque.status === 'void' && payment.cheque.void_reason && (
                                <p className="mt-1 max-w-[180px] text-xs leading-4 text-red-700" title={payment.cheque.void_reason}>
                                  {payment.cheque.void_reason}
                                </p>
                              )}
                            </div>
                          ) : <span className="text-xs text-[#062B1A]/40">Not created</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCreateCommissionCheque(payment)}
                            disabled={creatingChequePaymentId === payment.key}
                            className="mr-1 h-8"
                          >
                            {creatingChequePaymentId === payment.key ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <FileText className="mr-1 h-3.5 w-3.5" />}
                            {payment.cheque ? 'Open cheque' : (batchSize > 1 ? `Create cheque (${batchSize})` : 'Create cheque')}
                          </Button>
                          {!payment.cheque && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeletePaymentTarget(payment)}
                              disabled={deletingPaymentId === payment.key}
                              className="h-8 px-2 text-[#062B1A]/45 hover:bg-red-50 hover:text-red-700"
                              aria-label="Delete commission payment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      </LayoutSection>

      {commissionChequeEditor && (
        <ChequeEditorDialog
          cheque={commissionChequeEditor}
          onClose={() => setCommissionChequeEditor(null)}
          onSaved={refreshCommissionPayments}
          onDeleted={async () => {
            setCommissionRefreshToken(value => value + 1);
            await Promise.all([refreshCommissionPayments(), refreshAgent()]);
          }}
        />
      )}

      {/* Notes timeline */}
      <LayoutSection id="notes" controller={layoutController}>
      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <SectionTitle icon={StickyNote}>Notes</SectionTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <NotesThread
            isSuperAdmin={user?.role === 'admin' && user?.is_super_admin === true}
            notes={noteHistory}
            currentUserId={user?.id || ''}
            onAdd={handleAddNote}
            onEdit={handleEditNote}
            onDelete={handleDeleteNote}
            deletingId={deletingNoteId}
            emptyText="No notes yet. Add the first."
          />
        </CardContent>
      </Card>
      </LayoutSection>

      {/* portal account modal */}
      <Dialog open={portalAccountOpen} onOpenChange={(open) => (open ? setPortalAccountOpen(true) : closePortalAccount())}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Create agency portal account</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="agency-portal-first-name">First name</Label>
                <Input
                  id="agency-portal-first-name"
                  value={portalAccountForm.first_name}
                  onChange={setPortalAccountText('first_name')}
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agency-portal-last-name">Last name</Label>
                <Input
                  id="agency-portal-last-name"
                  value={portalAccountForm.last_name}
                  onChange={setPortalAccountText('last_name')}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="agency-portal-email">Email</Label>
              <Input
                id="agency-portal-email"
                type="email"
                value={portalAccountForm.email}
                onChange={setPortalAccountText('email')}
                className={inputClass}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agency-portal-password">Temporary password</Label>
              <Input
                id="agency-portal-password"
                type="password"
                autoComplete="new-password"
                value={portalAccountForm.password}
                onChange={setPortalAccountText('password')}
                className={inputClass}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closePortalAccount} disabled={creatingPortalAccount}>
              Cancel
            </Button>
            <Button
              onClick={handleCreatePortalAccount}
              disabled={creatingPortalAccount}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {creatingPortalAccount ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="mr-2 h-4 w-4" />
              )}
              Create account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </ProfileSectionGrid>

      {/* Edit agency modal */}
      <Dialog open={editOpen} onOpenChange={(open) => (open ? setEditOpen(true) : closeEdit())}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Edit agency</DialogTitle>
          </DialogHeader>

          {form && (
            <AgencyProfileForm
              form={form}
              setForm={setForm}
              meta={meta}
              inputClass={inputClass}
              sinPlaceholder={agent.sinMask || ''}
              customFields={customFields}
            />
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* payment delete confirm */}
      <AlertDialog
        open={Boolean(deletePaymentTarget)}
        onOpenChange={(open) => (!open && !deletingPaymentId ? setDeletePaymentTarget(null) : null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'var(--font-display)' }}>Delete commission payment?</AlertDialogTitle>
            <AlertDialogDescription>
              {(deletePaymentTarget?.payments?.length || 1) > 1
                ? `This permanently deletes all ${deletePaymentTarget.payments.length} student payments recorded together for this cheque.`
                : 'This permanently deletes the payment record.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingPaymentId)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCommissionPayment}
              disabled={Boolean(deletingPaymentId)}
              className="bg-[rgb(153,27,27)] text-white hover:bg-[rgb(127,29,29)]"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deletingPaymentId ? 'Deleting...' : 'Delete payment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* delete confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={(open) => (!deleting ? setDeleteOpen(open) : null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'var(--font-display)' }}>Delete agency?</AlertDialogTitle>
            <AlertDialogDescription>
              This will unlink its {studentCount} student{studentCount === 1 ? '' : 's'} and permanently delete {agent.company_name || 'this agency'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-[rgb(153,27,27)] text-white hover:bg-[rgb(127,29,29)]"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deleting ? 'Deleting...' : 'Delete agency'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AdminAgencyDetail() {
  return (
    <AgencyDetailErrorBoundary>
      <AdminAgencyDetailContent />
    </AgencyDetailErrorBoundary>
  );
}
